import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const errorBannerVariants = cva(
  'flex items-center gap-3 rounded-xl border',
  {
    variants: {
      variant: {
        error: 'bg-red-50 border-red-200 text-red-700',
        warning: 'bg-amber-50 border-amber-200 text-amber-700',
        info: 'bg-blue-50 border-blue-200 text-blue-700',
      },
      size: {
        sm: 'p-3 text-xs',
        md: 'p-4 text-sm',
      },
    },
    defaultVariants: {
      variant: 'error',
      size: 'md',
    },
  }
);

export interface ErrorBannerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof errorBannerVariants> {
  /** The error/warning message to display */
  message: string;
  /** Label for the retry button */
  retryLabel?: string;
  /** Called when the retry button is clicked */
  onRetry?: () => void;
}

const ErrorBanner = React.forwardRef<HTMLDivElement, ErrorBannerProps>(
  ({ className, variant, size, message, retryLabel = 'Try again', onRetry, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(errorBannerVariants({ variant, size }), className)}
        {...props}
      >
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <p className={size === 'sm' ? 'text-xs' : 'text-sm'}>{message}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="ml-auto text-sm font-medium underline hover:no-underline whitespace-nowrap"
          >
            {retryLabel}
          </button>
        )}
      </div>
    );
  }
);

ErrorBanner.displayName = 'ErrorBanner';

export { ErrorBanner, errorBannerVariants };
